import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import LabelSp from '../LabelSp';
import CalendarSp from '../CalendarSp';
import { cfgPtBr } from '../../config/Constantes';
import { ClienteModel } from '../../util/Models';
import DropdownSp from '../DropdownSp';
import InputTextSp from '../InputTextSp';
import InputCurrencySp from '../InputCurrencySp';
import ButtonSp from '../ButtonSp';
import { cloneObj, generateUUID } from '../../util/functions';
import DataTableSp from '../DataTableSp';
import ModuloService from '../../services/ModuloService';
import ClienteService from '../../services/ClienteService';
import ValoresContrato from './valoresContrato';

/**
 * @typedef {Object} PropsType
 * @property {any} contrato
 * @property {[]} modulos modulos
 * @property {boolean} viewMode estado da janela
 * @property {boolean} permissaoAdminAlteracao permissao de adm
 * @property {boolean} permissaoModulos permissao para alterar módulos
 * @property {(contrato: any) => void} setContrato Handle para salvar dados
 */

/**
 * @type {React.FC<PropsType>}
 */
function ContratoOrcamento({
  contrato,
  viewMode,
  permissaoAdminAlteracao,
  permissaoModulos,
  setContrato,
  dispositivosUsados,
  orcamento,
}) {
  ContratoOrcamento.propTCpes = {
    contrato: PropTypes.instanceOf(ClienteModel).isRequired,
    viewMode: PropTypes.func.isRequired,
    permissaoAdminAlteracao: PropTypes.bool.isRequired,
    permissaoModulos: PropTypes.bool.isRequired,
    setContrato: PropTypes.func.isRequired,
    dispositivosUsados: PropTypes.number.isRequired,
    orcamento: PropTypes.bool.isRequired,
  };

  const [modulos, setModulos] = useState([]);
  const [clienteModuloEdicao, setClienteModuloEdicao] = useState();
  const [showValoresContrato, setShowValoresContrato] = useState(false);
  const [moduloSelecionado, setModuloSelecionado] = useState();
  const [percDesconto, setPercDesconto] = useState(0);
  const [funcTimeOut, setFuncTimeOut] = useState();

  const calculaDesconto = useCallback(_contrato => {
    const desconto =
      (_contrato.percentualSalarioCalculado !== 0 &&
      _contrato.percentualSalarioCalculado > _contrato.percentualSalario
        ? 1 - _contrato.percentualSalario / _contrato.percentualSalarioCalculado
        : 0) * 100;

    setPercDesconto(desconto);
  }, []);

  const calculaContrato = useCallback(
    async contratoCalculo => {
      const contratoRet = await ClienteService.orcamentoContrato(contratoCalculo);

      const {
        percentualSalarioCalculado,
        modulosPercSalario,
        edicaoPercAcessoUltrapassado,
      } = contratoRet;
      let { percentualSalario } = contratoCalculo;

      if (orcamento) {
        percentualSalario = percentualSalarioCalculado;
      }
      let { valorMensal } = contratoCalculo;

      valorMensal = (percentualSalario / 100) * contratoCalculo.valorSalarioBase;
      contratoRet.modulos.forEach(m => {
        if (!m.uuid) {
          // como veio do servidor, algum pode nao ter o uuid
          m.uuid = generateUUID();
        }
      });

      const contratoAtualizado = {
        ...contratoCalculo,
        percentualSalarioCalculado,
        modulos: contratoRet.modulos,
        modulosPercSalario,
        edicaoPercAcessoUltrapassado,
        percentualSalario,
        valorMensal,
      };

      setContrato(contratoAtualizado);

      calculaDesconto(contratoAtualizado);
    },
    [calculaDesconto, orcamento, setContrato]
  );

  const autoCalculoContrato = useCallback(
    async contratoCalc => {
      if (funcTimeOut) {
        clearTimeout(funcTimeOut);
      }
      const func = setTimeout(async () => {
        calculaContrato(contratoCalc);
      }, 1000);
      setFuncTimeOut(func);
    },
    [funcTimeOut, calculaContrato]
  );

  function salvarModulo() {
    let idArray = -1;

    clienteModuloEdicao.idModulo = clienteModuloEdicao.modulo.id;
    idArray = contrato.modulos.findIndex(m => m.uuid === clienteModuloEdicao.uuid);
    if (idArray >= 0) {
      contrato.modulos[idArray] = clienteModuloEdicao;
    } else {
      contrato.modulos.push(clienteModuloEdicao);
    }

    contrato.percentualSalarioCalculado = 0;
    setContrato({ ...contrato });
    setModuloSelecionado(undefined);

    autoCalculoContrato(contrato);
  }

  function alterarModulo(clienteModulo) {
    const idxModulo = modulos.findIndex(e => e.value.id === clienteModulo.modulo.id);
    const mod = modulos[idxModulo].value;

    setClienteModuloEdicao(cloneObj({ ...clienteModulo }));
    setModuloSelecionado(mod);
  }

  const loadModulos = useCallback(async (idSistema, codigoEdicao) => {
    const retorno = await ModuloService.findBySistemaEdicao(idSistema, codigoEdicao);

    const lista = retorno.items.map(c => {
      return { label: c.descricao, value: c };
    });
    setModulos(lista);
  }, []);

  useEffect(() => {
    loadModulos(contrato.idSistema, contrato.edicao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Array.isArray(contrato.modulos)) {
      contrato.modulos.forEach(m => {
        m.uuid = generateUUID();
      });

      if (!contrato.percentualSalario || !contrato.percentualSalarioCalculado) {
        calculaContrato(contrato);
      }
      calculaDesconto(contrato);
      setContrato({ ...contrato });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const footer = (
    <div style={{ textAlign: 'center' }}>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={() => {
          setModuloSelecionado(undefined);
        }}
        className="p-button-danger"
      />
      <Button
        label="Confirmar"
        icon="pi pi-check"
        onClick={() => {
          salvarModulo();
        }}
      />
    </div>
  );

  return (
    <>
      {moduloSelecionado && (
        <Dialog
          closable={false}
          footer={footer}
          header="Qlteração de módulo"
          visible={!!moduloSelecionado}
          style={{ minWidth: '350px', maxWidth: '800px', width: '75%' }}
          modal
          onHide={() => {
            setModuloSelecionado(undefined);
          }}
        >
          <div className="p-grid">
            <div className="p-col-6 p-md-8 p-lg-8 p-fluid" style={{ display: 'block' }}>
              <LabelSp>Módulo</LabelSp>
              <InputTextSp disabled value={moduloSelecionado.descricao} />
            </div>

            <div className="p-col-3 p-md-2 p-lg-2 p-fluid">
              <LabelSp>Incluso</LabelSp>

              <InputCurrencySp
                digits={0}
                disabled
                value={clienteModuloEdicao?.qtdAcessoIncluso}
              />
            </div>
            <div className="p-col-3 p-md-2 p-lg-2 p-fluid">
              <LabelSp>Qtd. Usada</LabelSp>
              <InputCurrencySp
                autoFocus
                digits={0}
                value={clienteModuloEdicao?.quantidade}
                onChange={e => {
                  let quantidade = Number(e.target.value);
                  if (moduloSelecionado?.tipoCobranca === 0 && quantidade > 1) {
                    quantidade = 1;
                  }
                  setClienteModuloEdicao({
                    ...clienteModuloEdicao,
                    quantidade,
                  });
                }}
              />
            </div>
          </div>
        </Dialog>
      )}
      {showValoresContrato && (
        <ValoresContrato
          cliente={contrato}
          visible={showValoresContrato}
          setVisible={setShowValoresContrato}
        />
      )}
      {!orcamento && (
        <>
          <div className="p-col-4 p-md-3 p-lg-3 p-fluid">
            <LabelSp>Inicio Contrato</LabelSp>
            <CalendarSp
              showOnFocus={false}
              showIcon
              required
              locale={cfgPtBr}
              dateFormat="dd/mm/yy"
              disabled={viewMode() || !permissaoAdminAlteracao()}
              value={contrato.dataInicioContrato}
              appendTo={window.body}
              onChange={e => {
                setContrato({
                  ...contrato,
                  dataInicioContrato: e.target.value,
                });
              }}
            />
          </div>
          <div className="p-col-4 p-md-4 p-lg-3 p-fluid">
            <LabelSp>Fim Contrato</LabelSp>
            <CalendarSp
              showIcon
              required
              dateFormat="dd/mm/yy"
              disabled={viewMode() || !contrato.inativo || !permissaoAdminAlteracao()}
              value={contrato.dataFimContrato}
              appendTo={window.body}
              onChange={e => {
                setContrato({
                  ...contrato,
                  dataFimContrato: e.target.value,
                });
              }}
            />
          </div>
          <div className="p-col-4 p-md-4 p-lg-3 p-fluid">
            <LabelSp>Vencto</LabelSp>
            <DropdownSp
              options={[
                { value: 10, label: 'Dia 10' },
                { value: 20, label: 'Dia 20' },
              ]}
              disabled={viewMode() || !permissaoModulos() || !permissaoAdminAlteracao()}
              placeholder="Selecione"
              required
              value={contrato.diaVencimento}
              onChange={e => {
                setContrato({
                  ...contrato,
                  diaVencimento: e.target.value,
                });
              }}
            />
          </div>
        </>
      )}

      <div className="p-col-4 p-md-4 p-lg-3 p-fluid">
        <LabelSp>Sistema</LabelSp>
        <InputTextSp disabled name="txtSistema" value={contrato.sistema.nome} />
      </div>
      <div className="p-col-4 p-md-4 p-lg-3 p-fluid">
        <LabelSp>Vr. Implant.</LabelSp>
        <InputCurrencySp
          digits={2}
          disabled={viewMode() || !permissaoModulos() || !permissaoAdminAlteracao()}
          name="txtValorImplantacao"
          value={contrato.valorImplantacao}
          onChange={(e, v) => {
            setContrato({
              ...contrato,
              valorImplantacao: v,
            });
          }}
        />
      </div>
      <div className="p-col-4 p-md-4 p-lg-3 p-fluid">
        <LabelSp>Sálario Base</LabelSp>
        <InputCurrencySp
          digits={2}
          disabled
          name="txtValorImplantacao"
          value={contrato.valorSalarioBase}
          onChange={(e, v) => {
            setContrato({
              ...contrato,
              valorSalarioBase: v,
            });
          }}
        />
      </div>

      <div className="p-col-4 p-md-4 p-lg-3 p-fluid" style={{ margin: 0, padding: 0 }}>
        <div style={{ margin: 0, padding: 0, display: 'flex' }}>
          <div style={{ margin: 0, padding: 3, flex: 1 }}>
            <LabelSp>% Calc.</LabelSp>
            <InputCurrencySp
              className="p-error"
              digits={4}
              disabled
              value={contrato.percentualSalarioCalculado}
            />
          </div>
          <div style={{ margin: 0, padding: 3, paddingRight: 0, paddingLeft: 0 }}>
            <LabelSp style={{ display: 'block' }}>&#160;</LabelSp>
            <ButtonSp
              icon="pi pi-dollar"
              title="Calcula contrato"
              disabled={viewMode()}
              onClick={() => {
                calculaContrato(contrato);
              }}
              type="button"
              className="p-button-secondary"
            />
          </div>
        </div>
      </div>

      <div className="p-col-4 p-md-4 p-lg-3 p-fluid">
        <LabelSp>%Desconto</LabelSp>
        <InputCurrencySp
          digits={4}
          disabled={viewMode() || !permissaoModulos() || !permissaoAdminAlteracao()}
          onChange={(e, v) => {
            const percDesc = v >= 0 ? v : 0;
            const percentualSalario =
              contrato.percentualSalarioCalculado * (1 - percDesc / 100);
            const contratoResult = {
              ...contrato,
              percentualSalario,
            };
            setContrato(contratoResult);
            setPercDesconto(percDesc);
          }}
          value={percDesconto}
        />
      </div>
      <div className="p-col-4 p-md-4 p-lg-3 p-fluid">
        <LabelSp>%Salário</LabelSp>
        <InputCurrencySp
          digits={4}
          disabled={viewMode() || !permissaoModulos() || !permissaoAdminAlteracao()}
          value={contrato.percentualSalario}
          onChange={(e, v) => {
            let { valorMensal } = contrato;
            if (v !== contrato.percentualSalario && contrato.valorSalarioBase > 0) {
              valorMensal = (v / 100) * contrato.valorSalarioBase;
            }

            const contratoResult = {
              ...contrato,
              percentualSalario: v,
              valorMensal,
            };
            calculaDesconto(contratoResult);
            setContrato(contratoResult);
          }}
        />
      </div>

      <div className="p-col-4 p-md-4 p-lg-3 p-fluid" style={{ margin: 0, padding: 0 }}>
        <div style={{ margin: 0, padding: 0, display: 'flex' }}>
          <div style={{ margin: 0, padding: 3, flex: 1 }}>
            <LabelSp>Vr. Mensal</LabelSp>
            <InputCurrencySp
              digits={2}
              disabled={viewMode() || !permissaoModulos() || !permissaoAdminAlteracao()}
              value={contrato.valorMensal}
              onChange={(e, v) => {
                let { percentualSalario } = contrato;
                if (v !== contrato.valorMensal && contrato.valorSalarioBase > 0) {
                  if (v > 0) {
                    percentualSalario = (v / contrato.valorSalarioBase) * 100;
                  } else {
                    percentualSalario = 0;
                  }
                }

                setContrato({
                  ...contrato,
                  percentualSalario,
                  valorMensal: v,
                });
              }}
            />
          </div>

          <div style={{ margin: 0, padding: 3, paddingRight: 0, paddingLeft: 0 }}>
            <LabelSp style={{ display: 'block' }}>&#160;</LabelSp>
            <ButtonSp
              icon="pi pi-info-circle"
              title="Detalhes sobre o cálculo"
              onClick={async () => {
                if (!viewMode()) {
                  await calculaContrato(contrato);
                }
                setShowValoresContrato(true);
              }}
              type="button"
              className="p-button-secondary"
            />
          </div>
        </div>
      </div>

      {renderLicencas()}
    </>
  );

  function renderLicencas() {
    return (
      <>
        <div className="p-col-12 p-md-12 p-lg-12 p-fluid" style={{ margin: 0, padding: 0 }}>
          <div className="p-grid" style={{ margin: 0, padding: 0 }}>
            <div className="p-col-12 p-fluid">
              <LabelSp>Dados edição e dispositivos</LabelSp>
              <hr style={{ marginTop: 3 }} />
            </div>

            <div className="p-col-5 p-md-6 p-lg-6 p-fluid">
              <LabelSp>Edição</LabelSp>
              <DropdownSp
                options={[
                  { label: 'Business', value: 0 },
                  { label: 'Professional', value: 1 },
                  { label: 'Lite', value: 2 },
                  { label: 'Nfe', value: 3 },
                ]}
                disabled={viewMode() || !permissaoModulos() || !permissaoAdminAlteracao()}
                required
                onChange={e => {
                  loadModulos(contrato.idSistema, e.target.value);
                  setContrato({
                    ...contrato,
                    percentualSalarioCalculado: 0,
                    edicao: e.target.value,
                  });
                  autoCalculoContrato({
                    ...contrato,
                    percentualSalarioCalculado: 0,
                    edicao: e.target.value,
                  });
                }}
                value={contrato.edicao}
              />
            </div>

            <div className="p-col-4 p-md-3 p-lg-3 p-fluid">
              <LabelSp>Qt. Disp.</LabelSp>
              <InputTextSp
                type="tel"
                value={contrato.numeroDispositivos}
                required
                disabled={viewMode() || !permissaoModulos() || !permissaoAdminAlteracao()}
                onChange={e => {
                  setContrato({
                    ...contrato,
                    percentualSalarioCalculado: 0,
                    numeroDispositivos: e.target.value,
                  });
                  autoCalculoContrato({
                    ...contrato,
                    percentualSalarioCalculado: 0,
                    numeroDispositivos: e.target.value,
                  });
                }}
              />
            </div>

            <div className="p-col-4 p-md-3 p-lg-3 p-fluid">
              <LabelSp>Usados</LabelSp>
              <InputTextSp type="tel" value={dispositivosUsados} required disabled />
            </div>

            <div className="p-col-12 p-fluid">
              <LabelSp>Dados dos Módulos</LabelSp>
              <hr style={{ marginTop: 3 }} />
            </div>
          </div>

          <div className="p-col-12 p-fluid">
            <DataTableSp value={contrato.modulos} style={{ marginBottom: '2px' }} responsive>
              <Column
                className="grid-col"
                header="Permitido"
                style={{ width: 80, textAlign: 'center' }}
                body={rowData => {
                  return (
                    <span style={{ color: rowData.permitido ? 'blue' : 'red' }}>
                      {rowData.permitido ? 'Sim' : 'Não'}
                    </span>
                  );
                }}
              />
              <Column
                className="grid-col"
                header="Descrição"
                body={rowData => rowData.modulo.descricao}
              />

              <Column
                className="grid-col-val"
                style={{ width: 100 }}
                header="Qtd. Incluso"
                body={rowData => rowData.qtdAcessoIncluso}
              />

              <Column
                className="grid-col-val"
                style={{ width: 100 }}
                header="Qtd. Usada"
                body={rowData => rowData.quantidade}
              />

              <Column
                className="gid-col-acoes-35"
                bodyStyle={{ textAlign: 'end' }}
                body={rowData => (
                  <Button
                    disabled={
                      !rowData.permitido ||
                      viewMode() ||
                      !permissaoModulos() ||
                      !permissaoAdminAlteracao()
                    }
                    type="button"
                    title="Alterar"
                    className="botao-pequeno p-button-success"
                    icon="pi pi-pencil"
                    onClick={() => {
                      alterarModulo(rowData);
                    }}
                  />
                )}
              />
            </DataTableSp>
          </div>
        </div>
      </>
    );
  }
}

export default ContratoOrcamento;
